import type { Turn } from "../../nest/generated"
import { parseTurnInput } from "../../nest/turnInput"
import type { QueuedTurn } from "../../types"
import { ACTIVE_TURN_STATUSES, type NestTurnRuntimeUpdate } from "./RuntimeManager"

const ATTACHMENT_PLACEHOLDER = "[image attachment]"

export interface RuntimeReconciliationDeps {
    listLiveTurns(): Promise<{ turns: Turn[] }>
    findTaskIdForSession(sessionId: string): string | null
    applyRuntime(taskId: string, turn: Turn): void
    seedQueuedTurn(taskId: string, turn: QueuedTurn): void
    reconcileRuntimes(liveTurnIds: ReadonlySet<string>): number
}

export interface RuntimeReconciliationReport {
    liveTurnsScanned: number
    matchedTurns: number
    unmatchedTurns: number
    appliedRuntimes: number
    seededQueuedTurns: number
    clearedRuntimes: number
}

export function turnRuntimeUpdateFromNestTurn(turn: Turn): NestTurnRuntimeUpdate {
    return {
        turnId: turn.turnId,
        sessionId: turn.sessionId,
        status: turn.status,
        queuedAt: turn.queuedAt,
        startedAt: turn.startedAt,
        completedAt: turn.completedAt,
        error: turn.error,
    }
}

export function queuedTurnFromNestTurn(turn: Turn): QueuedTurn | null {
    if (turn.status !== "queued") return null

    const turnInput = parseTurnInput(turn.input)
    const texts = turnInput?.parts.flatMap((part) => (part.type === "text" ? [part.text] : [])) ?? []
    const timestamp = turn.queuedAt ?? new Date().toISOString()
    return {
        id: turn.turnId,
        sessionId: turn.sessionId,
        type: "do",
        input: texts.length > 0 ? texts.join("\n") : ATTACHMENT_PLACEHOLDER,
        status: "queued",
        createdAt: timestamp,
        updatedAt: timestamp,
        clientRequestId: turn.clientRequestId,
        ...(turnInput ? { turnInput } : {}),
    }
}

export async function reconcileRuntimeState(deps: RuntimeReconciliationDeps): Promise<RuntimeReconciliationReport> {
    const report: RuntimeReconciliationReport = {
        liveTurnsScanned: 0,
        matchedTurns: 0,
        unmatchedTurns: 0,
        appliedRuntimes: 0,
        seededQueuedTurns: 0,
        clearedRuntimes: 0,
    }
    const result = await deps.listLiveTurns()
    report.liveTurnsScanned = result.turns.length

    for (const turn of result.turns) {
        const taskId = deps.findTaskIdForSession(turn.sessionId)
        if (!taskId) {
            report.unmatchedTurns += 1
            continue
        }
        report.matchedTurns += 1

        if (ACTIVE_TURN_STATUSES.has(turn.status)) {
            deps.applyRuntime(taskId, turn)
            report.appliedRuntimes += 1
        }

        const queuedTurn = queuedTurnFromNestTurn(turn)
        if (queuedTurn) {
            deps.seedQueuedTurn(taskId, queuedTurn)
            report.seededQueuedTurns += 1
        }
    }

    report.clearedRuntimes = deps.reconcileRuntimes(new Set(result.turns.map((turn) => turn.turnId)))

    return report
}
